import { ComProtocol, ConnectionState } from '@iotize/tap/protocol/api';
import { QueueComProtocol } from '@iotize/tap/protocol/core';
import { defer, Observable } from 'rxjs';
import { filter, first, map, share } from 'rxjs/operators';
import { BleConfig } from './ble-config';
import { BLEPacketBuilder } from './ble-packet-builder';
import { BLEPacketSplitter } from './ble-packet-splitter';
import { debug } from './debug';
import { BleComError } from './errors';
import { sanitizeUUID } from './util';

export namespace UniversalBleProtocolAdapter {
  export interface Options {
    /**
     * BLE maximum transfer unit size (in bytes)
     */
    mtu: number;
    /**
     * Maximum buffer length for received frames
     */
    maximumBufferLength: number;
    /**
     * True to wait for write characteristic acknowledge
     * According to firmware implementation, write acknowledge is not
     * always returns, leading to a timeout exception in client
     */
    waitForWriteAcknowledge: boolean;
    /**
     * TapNLinks may have 2 different BLE characteristic to communicate
     * You can define which one should be used in priority
     * If it does not exist, it will use the other one
     */
    preferedComServiceType?: 'legacy' | 'large-frame';
    /**
     * True if you want to remove '-' character from ID
     */
    sanitizeUUID?: boolean;
  }
}

export const DEFAULT_BLE_OPTIONS: UniversalBleProtocolAdapter.Options = {
  mtu: BleConfig.maxPacketLengthWithoutOffset + 1,
  maximumBufferLength: 255,
  waitForWriteAcknowledge: true,
  preferedComServiceType: 'large-frame',
  sanitizeUUID: false,
};

/**
 * BLE communication
 *
 * With ble communication, data is split into sub packets.
 * This class handles creation of packet chunks.
 *
 * - You must only implement the function to send one packet chunk writeLwm2mPacketChunk()
 * -
 */
export class UniversalBleProtocolAdapter<
  T extends ServiceAdapterInterface = ServiceAdapterInterface,
> extends QueueComProtocol {
  private _lwm2mCharc?: CharacteristicAdapterInterface;
  private _useSplitter = true;
  private _readPromise?: Promise<Uint8Array>;
  public bleOptions: UniversalBleProtocolAdapter.Options;

  private _unexpectedBleDisconnection = this.peripheral.stateChange.pipe(
    filter((newState) => {
      const currentProtocolConnectionState = this.getConnectionState();
      return (
        newState === ConnectionState.DISCONNECTED &&
        currentProtocolConnectionState !== ConnectionState.DISCONNECTING &&
        currentProtocolConnectionState !== ConnectionState.DISCONNECTED
      );
    }),
    share(),
  );

  constructor(
    public peripheral: PeripheralAdapterInterface<T>,
    bleOptions: Partial<UniversalBleProtocolAdapter.Options> = {},
  ) {
    super();
    this.bleOptions = { ...DEFAULT_BLE_OPTIONS, ...bleOptions };
    this.options.connect.timeout = 8000;
    this.options.send.timeout = 4000;
    this.options.disconnect.timeout = 8000;
    this._unexpectedBleDisconnection.subscribe(async () => {
      debug(
        'unexpected BLE disconnection detected. Running proper BLE disconnection process',
      );
      await this.disconnect()
        .toPromise()
        .catch((err) => {
          debug(
            `Proper BLE disconnection process failed with error: ${err.message}`,
          );
        });
    });
  }

  get lwm2mCharc(): CharacteristicAdapterInterface {
    if (!this._lwm2mCharc) {
      this.setConnectionState(ConnectionState.DISCONNECTED);
      throw ComProtocol.Errors.notConnected({
        protocol: this,
      });
    }
    return this._lwm2mCharc;
  }

  private sanitizeUUID(uuid: string) {
    return this.bleOptions.sanitizeUUID ? sanitizeUUID(uuid) : uuid;
  }

  _connect(): Observable<any> {
    return defer(async () => {
      try {
        await this.peripheral.connect();
        this._lwm2mCharc = await this.setupLwm2mCharacteristic();
      } catch (err) {
        try {
          await this.peripheral.disconnect();
        } catch (err) {
          debug(
            'Failed to propertly disconnect after connection failed',
            (err as Error).message,
          );
        }
        throw err;
      }
    }).pipe(share());
  }

  _disconnect(): Observable<any> {
    return defer(async () => {
      if (this.peripheral) {
        try {
          await this.peripheral.disconnect();
        } catch (err) {
          console.warn(`Failed to properly disconnect from peripheral`, err);
        }
      }
      this._lwm2mCharc = undefined;
    }).pipe(share());
  }

  private async setupLwm2mCharacteristic(): Promise<CharacteristicAdapterInterface> {
    const lwm2mServiceUUIDs = [
      this.sanitizeUUID(BleConfig.services.lwm2m.service),
    ];
    if (this.bleOptions.preferedComServiceType === 'large-frame') {
      lwm2mServiceUUIDs.push(
        this.sanitizeUUID(BleConfig.services.fastLwm2m.service),
      );
    }

    const serviceMap =
      await this.peripheral.discoverServices(lwm2mServiceUUIDs);
    debug(
      'Found services ',
      Object.keys(serviceMap).join(', '),
      'asked for services: ',
      lwm2mServiceUUIDs.join(', '),
    );
    const charac = await this._selectLwm2mCharacteristic(serviceMap);
    await charac.enableNotifications(true);
    return charac;
  }

  private async _selectLwm2mCharacteristic(
    serviceMap: Partial<Record<string, T>>,
  ): Promise<CharacteristicAdapterInterface> {
    const largeFrameServiceUUID = this.sanitizeUUID(
      BleConfig.services.fastLwm2m.service,
    );
    const legacyLwm2mServiceUUID = this.sanitizeUUID(
      BleConfig.services.lwm2m.service,
    );
    if (
      this.bleOptions.preferedComServiceType === 'legacy' &&
      serviceMap[legacyLwm2mServiceUUID]
    ) {
      debug(
        'Force usage of legacy lwm2m characteristic UUID: ' +
          legacyLwm2mServiceUUID,
      );
      return this._getLegacyLwm2mCharacteristic(
        serviceMap[legacyLwm2mServiceUUID]!,
      );
    } else if (serviceMap[largeFrameServiceUUID]) {
      debug('Found fast lwm2m characteristic UUID: ' + largeFrameServiceUUID);
      return this._getLargeFrameLwm2mCharacteristic(
        serviceMap[largeFrameServiceUUID]!,
      );
    } else if (serviceMap[legacyLwm2mServiceUUID]) {
      debug(
        'Found legacy lwm2m characteristic UUID: ' + legacyLwm2mServiceUUID,
      );
      return this._getLegacyLwm2mCharacteristic(
        serviceMap[legacyLwm2mServiceUUID]!,
      );
    } else {
      debug(
        `No LwM2M service found. Available services: ${Object.keys(
          serviceMap,
        ).join(', ')}`,
      );
      throw BleComError.serviceNotFound(legacyLwm2mServiceUUID);
    }
  }

  private async _getLargeFrameLwm2mCharacteristic(
    service: T,
  ): Promise<
    CharacteristicAdapterInterface | PromiseLike<CharacteristicAdapterInterface>
  > {
    this.bleOptions.mtu = 255; // TODO read from device
    this._useSplitter = false;
    return await service.getCharacteristic(
      this.sanitizeUUID(BleConfig.services.fastLwm2m.charac),
    );
  }

  private async _getLegacyLwm2mCharacteristic(
    service: T,
  ): Promise<
    CharacteristicAdapterInterface | PromiseLike<CharacteristicAdapterInterface>
  > {
    this.bleOptions.mtu = BleConfig.maxPacketLengthWithoutOffset + 1;
    this._useSplitter = true;
    return await service.getCharacteristic(
      this.sanitizeUUID(BleConfig.services.lwm2m.charac),
    );
  }

  async read(): Promise<Uint8Array> {
    // debug('read()...');
    if (!this._readPromise) {
      this._readPromise = this._createReadPromise();
    }
    return this._readPromise;
  }

  async readUnit(): Promise<Uint8Array> {
    try {
      const result = await this.lwm2mCharc.data
        .pipe(
          first(),
          map((info) => info.data),
        )
        .toPromise();
      if (!result) {
        return new Uint8Array();
      }
      return result;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  async write(data: Uint8Array): Promise<any> {
    // debug('write()...');
    this._readPromise = this._createReadPromise();
    if (this.useSplitter) {
      const chunks = BLEPacketSplitter.wrapWithChecksum(
        data,
        this.chunkSize,
      ).getPackets();
      for (const chunk of chunks) {
        await this.writeUnit(chunk);
      }
    } else {
      return this.writeUnit(data);
    }
  }

  get useSplitter() {
    return this._useSplitter;
  }

  get chunkSize(): number {
    return this.bleOptions.mtu - 1;
  }

  async writeUnit(data: Uint8Array): Promise<any> {
    try {
      if (data.length > this.bleOptions.mtu) {
        throw BleComError.writeSizeAboveMTU(data, this.bleOptions.mtu);
      }
      if (this.bleOptions.waitForWriteAcknowledge) {
        return this.lwm2mCharc.write(data, true);
      } else {
        this.lwm2mCharc.write(data, true).catch((err) => {
          console.warn(`Write error ignored`, err);
        });
      }
    } catch (err) {
      return Promise.reject(err);
    }
  }

  private async _createReadPromise(): Promise<Uint8Array> {
    // debug(`_createReadPromise()`);
    if (this.useSplitter) {
      const packetBuilder = new BLEPacketBuilder(
        this.bleOptions.maximumBufferLength,
      );
      while (!packetBuilder.hasAllChunks()) {
        const chunk = await this.readUnit();
        packetBuilder.append(chunk);
      }
      if (packetBuilder.isChecksumValid()) {
        return packetBuilder.getData();
      } else {
        throw BleComError.invalidBleChunkChecksum(packetBuilder);
      }
    } else {
      return this.readUnit();
    }
  }
}

export interface PeripheralAdapterInterface<
  T extends ServiceAdapterInterface = ServiceAdapterInterface,
> {
  state: string;

  stateChange: Observable<ConnectionState>;

  name?: string;

  id: string;

  /**
   * Discover services
   * @param serviceUUIDs if not provided it will discover all services, if provided it will discover the given services
   * @returns returns a map of services discovered. If a listed service is not discovered, it will be set to undefined
   */
  discoverServices<Key extends string = string>(
    serviceUUIDs?: Key[],
  ): Promise<Partial<Record<Key, T>>>;

  connect(): Promise<void>;

  disconnect(): Promise<void>;

  getService(uuid: string): Promise<T>;
}

export interface CharacteristicProperties {
  readonly broadcast: boolean;
  readonly read: boolean;
  readonly writeWithoutResponse: boolean;
  readonly write: boolean;
  readonly notify: boolean;
  readonly indicate: boolean;
  readonly authenticatedSignedWrites: boolean;
  readonly reliableWrite: boolean;
  readonly writableAuxiliaries: boolean;
}

export interface ServiceAdapterInterface<
  CharacteristicType extends
    CharacteristicAdapterInterface = CharacteristicAdapterInterface,
> {
  uuid: string;

  /**
   * Get characteristic identified by the given UUID
   * @throws if characteristic does not exist
   *
   * @param charcUUID
   */
  getCharacteristic(charcUUID: string): Promise<CharacteristicType>;

  getCharacteristics(): Promise<CharacteristicType[]>;
}

export interface CharacteristicAdapterInterface<
  DescriptorType extends
    DescriptorAdapterInterface = DescriptorAdapterInterface,
> {
  uuid: string;

  properties: CharacteristicProperties; // TODO

  data: Observable<{ data: Uint8Array; isNotification: boolean }>;

  /**
   * Write characteristic value
   * If successful, returns the written data
   * @param data
   * @param writeWithoutResponse
   */
  write(data: Uint8Array, writeWithoutResponse: boolean): Promise<Uint8Array>;

  /**
   * Read characteristic value
   */
  read(): Promise<Uint8Array>;

  /**
   * Start/Stop notifications with indication or notification accordording to
   * characteristic configuration
   * @param enabled
   */
  enableNotifications(enabled: boolean): Promise<void>;

  /**
   * Get descriptors
   */
  getDescriptors(): Promise<DescriptorType[]>;

  // /**
  //  * Get given descriptor
  //  * throws if descriptor does not exist
  //  * @param descriptorUUID
  //  */
  // getDescriptor(descriptorUUID: string): Promise<DescriptorType>;
}

export interface DescriptorAdapterInterface {
  /**
   * Descriptor UUID
   */
  uuid: string;
  /**
   * Write descriptor value
   */
  writeValue(data: Uint8Array): Promise<void>;
  /**
   * Read descriptor value
   */
  readValue(): Promise<Uint8Array>;
}
